import {
  CallTranscriptData,
  CallTranscriptMessage,
  getCallTranscript,
  getCallTranscriptCompanyAlert,
  getCallTranscriptOneTime,
} from '@/apis/shipment';
import { Avatar, Divider, Flex, message } from 'antd';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { AiStar } from '../../../assets/icons/icons';
import phoneWhite from '../../../assets/images/AiphoneWhite.svg';
import avatar from '../../../assets/images/pocavatar.svg';
import { useSpin } from '../../../components/SpinContext';
import ToggleSwitch from '../../../components/ToggleSwitch';
import './Transcripts.css';
import AIAlertDetails, { FooterInfo } from './ui/AIAlertDetails';
import CallRecordingPlayer from './ui/CallRecordingPlayer';
import SectionTitle from './ui/SectionTitle';
import { capitalize } from './utils';

// Type definitions
interface AiEvent {
  id: string;
}

interface Alert {
  id?: string;
  notification_type?: string;
  ai_events?: AiEvent[];
  eventId?: string;
  transcriptID?: string;
}

// Component prop interfaces
interface TranscriptListProps {
  transcript?: CallTranscriptMessage[];
  agentName?: string;
  contactName?: string;
  isEscalationCall?: boolean;
}

interface InfoBlockProps {
  icon?: React.ReactNode;
  avatar?: string;
  title: string;
  time: string;
  text?: string;
  isFirst?: boolean;
}

interface AiCallTranscriptProps {
  alert: Alert;
  token: string;
  setAlertId?: (id: string | null) => void;
  unauthorize?: (error: unknown) => void;
  onClose?: (closeAll: boolean) => void;
  onDismiss?: () => void;
  onResolve?: () => void;
  onOpenAgent?: (agentId: string, readOnly: boolean) => void;
  key?: number;
  shipmentId: string;
  showHeaderButtons?: boolean;
}

/**
 * Renders a list of transcript messages in a chat-like format
 *
 * @param transcript - Array of transcript messages
 * @param agentName - Name of the AI agent
 * @param contactName - Name of the contact
 */
const TranscriptList: React.FC<TranscriptListProps> = ({
  transcript,
  agentName,
  contactName,
  isEscalationCall,
}) => {
  const seen = { agent: false, user: false };
  const keyCounts = new Map<string, number>();

  return (
    <div className='chat-scroll-container'>
      {transcript?.map((msg: CallTranscriptMessage, i: number) => {
        const baseKey = `${msg.sender}:${msg.text}`;
        const repeat = keyCounts.get(baseKey) ?? 0;
        keyCounts.set(baseKey, repeat + 1);
        const senderType =
          msg.sender?.toLowerCase() === 'agent' ? 'agent' : 'user';
        const outgoing = senderType === 'agent';
        const shouldShowName = !seen[senderType];
        if (shouldShowName) seen[senderType] = true;
        const senderName = outgoing
          ? agentName || 'AI assistant'
          : contactName || 'Recipient';
        const isFirstBubble = i === 0;
        const bubbleClass = [
          'chat-bubble',
          outgoing ? 'outgoing' : 'incoming',
          isEscalationCall && isFirstBubble ? 'escalation-first' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={`${baseKey}:${repeat}`}>
            <Flex justify={outgoing ? 'flex-end' : 'flex-start'}>
              <div className={bubbleClass}>
                {msg.text}
                {outgoing ? (
                  <img
                    alt='call'
                    src={phoneWhite}
                    className='chat-avatar outgoing'
                  />
                ) : (
                  <img
                    alt='contact'
                    src={avatar}
                    className='chat-avatar incoming'
                  />
                )}
              </div>
            </Flex>
            {shouldShowName && (
              <Flex
                justify={outgoing ? 'flex-end' : 'flex-start'}
                className='chat-sender'
              >
                <div className='chat-sender-name chat-sender-name-inset'>
                  {senderName}
                </div>
              </Flex>
            )}
          </div>
        );
      })}
      {(transcript?.length ?? 0) === 0 && (
        <p className='ai-transcript-empty'>No transcript available</p>
      )}
    </div>
  );
};

/**
 * Displays an info block with icon/avatar, title, time and optional text
 *
 * @param icon - React node for icon display
 * @param avatar - Avatar text/initials
 * @param title - Block title
 * @param time - Timestamp text
 * @param text - Optional description text
 * @param isFirst - Whether this is the first block (adds divider)
 */
const InfoBlock: React.FC<InfoBlockProps> = ({
  icon,
  avatar,
  title,
  time,
  text,
  isFirst,
}) => (
  <div className={isFirst ? 'ai-infoblock' : undefined}>
    <Flex gap={12} align='flex-start'>
      <div className='ai-infoblock-icon'>
        {avatar ? (
          <Avatar style={{ backgroundColor: '#F5E08B', color: '#FFF' }}>
            {avatar}
          </Avatar>
        ) : (
          icon
        )}
      </div>

      <div className='flex-1 w-full'>
        <Flex
          justify='space-between'
          align='center'
          className='mb-1'
          style={{ width: '100%' }}
        >
          <span className='ai-infoblock-title'>{title}</span>
          <span className='ai-footer'>{time}</span>
        </Flex>

        {text && <div className='ai-infoblock-text'>{text}</div>}
        {isFirst && <Divider />}
      </div>
    </Flex>
  </div>
);

/**
 * Main component for displaying AI call transcript details and conversation
 *
 * @param alert - Alert data containing transcript information
 * @param token - Authentication token for API calls
 * @param unauthorize - Callback for handling unauthorized errors
 * @param onClose - Callback for closing the transcript view
 * @param onDismiss - Callback for dismissing the alert
 * @param onResolve - Callback for resolving the alert
 */
const AiCallTranscript: React.FC<AiCallTranscriptProps> = ({
  alert,
  token,
  unauthorize,
  onClose,
  setAlertId,
  onDismiss,
  onResolve,
  onOpenAgent,
  key,
  shipmentId,
  showHeaderButtons = true,
}) => {
  const [transcriptData, setTranscriptData] =
    useState<CallTranscriptData | null>(null);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { showSpin, hideSpin } = useSpin();

  const preferredLang = useMemo(
    () =>
      capitalize(transcriptData?.company_user_preferred_language || 'English'),
    [transcriptData]
  );
  useEffect(() => {
    const isOneTime = alert?.notification_type === 'one_time_notification';
    const isCompanyAlert = alert?.notification_type === 'company_alert';
    const transcriptId = isOneTime
      ? alert?.eventId
      : isCompanyAlert
        ? alert?.id
        : alert?.transcriptID || alert?.id;
    if (!transcriptId) return;
    setTranscriptData(null);
    showSpin();
    if (isOneTime) {
      getCallTranscriptOneTime(token, transcriptId, isChecked, shipmentId)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            setTranscriptData(data.data);
          } else {
            message.error(data?.message || 'Unable to load transcript.');
          }
        })
        .catch((err) => {
          setTranscriptData(null);
          unauthorize?.(err);
        })
        .finally(() => hideSpin());
    } else if (isCompanyAlert) {
      getCallTranscriptCompanyAlert(token, transcriptId, isChecked)
        .then((res) => {
          const data = res?.data;
          if (data?.data?.is_success) {
            setTranscriptData(data.data.data);
          } else {
            message.error(data?.data?.message || 'Unable to load transcript.');
          }
        })
        .catch((err) => {
          setTranscriptData(null);
          unauthorize?.(err);
        })
        .finally(() => hideSpin());
    } else {
      getCallTranscript(token, transcriptId, isChecked)
        .then((res) => {
          const data = res?.data;
          if (data?.data?.is_success) {
            setTranscriptData(data.data.data);
            setAlertId?.(data?.data?.data?.alert_id ?? null);
          } else {
            message.error(data?.data?.message || 'Unable to load transcript.');
          }
        })
        .catch((err) => {
          setTranscriptData(null);
          unauthorize?.(err);
        })
        .finally(() => hideSpin());
    }
  }, [
    alert,
    token,
    isChecked,
    showSpin,
    unauthorize,
    hideSpin,
    setAlertId,
    key,
    shipmentId,
  ]);

  const shouldShowNextStep = transcriptData?.show_buttons;

  return (
    <Fragment>
      <AIAlertDetails
        isCritical={transcriptData?.is_critical ?? false}
        alert={alert}
        subtitle='AI call'
        title={
          alert?.notification_type === 'one_time_notification'
            ? 'One-time message'
            : (transcriptData?.title ?? '')
        }
        alertTitle={
          alert?.notification_type === 'one_time_notification'
            ? 'One-time message'
            : (transcriptData?.alert_title ?? '')
        }
        pillLabel={transcriptData?.status_info?.description}
        triggeredBy={
          transcriptData?.triggered_by_user_name || transcriptData?.triggered_by
        }
        recipient={transcriptData?.recipients}
        message={transcriptData?.message_content}
        sentimentLabel={transcriptData?.sentiment_analysis?.type}
        summary={transcriptData?.summary || 'No summary provided.'}
        agentId={transcriptData?.agent_id}
        agentName={transcriptData?.agent_name}
        agentPurpose={transcriptData?.agent_purpose}
        showNextStep={shouldShowNextStep}
        nextStep={
          transcriptData?.next_step ||
          (transcriptData?.contact_info?.phone
            ? `Call ${transcriptData?.contact_info?.name} at ${transcriptData?.contact_info?.phone} to resolve`
            : `Contact not provided for ${transcriptData?.contact_info?.name || 'recipient'}`)
        }
        onDismiss={onDismiss}
        onResolve={onResolve}
        onClose={(closeAll) => onClose?.(closeAll)}
        onOpenAgent={onOpenAgent}
        bound_caller={transcriptData?.bound_caller}
        showHeaderButtons={showHeaderButtons}
      />
      {transcriptData?.audio_url && (
        <CallRecordingPlayer src={transcriptData.audio_url} />
      )}

      <SectionTitle
        right={
          <Flex align='center' gap={8}>
            <ToggleSwitch
              label=''
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            <span className='text-xs'>Show in {preferredLang}</span>
          </Flex>
        }
      >
        <span className='font-semibold text-base'>Call transcript</span>
      </SectionTitle>

      {isChecked && (
        <div className='ai-translation-note'>
          Message is a translation to recipient’s preferred language of{' '}
          {transcriptData?.company_user_preferred_language || 'English'}.
        </div>
      )}

      <TranscriptList
        transcript={transcriptData?.transcript}
        agentName={transcriptData?.agent_name}
        contactName={transcriptData?.primary_contact_name}
        isEscalationCall={transcriptData?.is_escalation_call}
      />

      <FooterInfo
        timestamp={transcriptData?.timestamp}
        duration={transcriptData?.call_duration}
        comments={
          Array.isArray(transcriptData?.comments)
            ? transcriptData.comments.map((item) => ({
                ...item,
                author_badge_color:
                  transcriptData?.author_badge_color || '#ccc',
              }))
            : transcriptData?.comments
              ? [
                  {
                    ...transcriptData.comments,
                    author_badge_color:
                      transcriptData?.author_badge_color || '#ccc',
                  },
                ]
              : []
        }
      />
      {transcriptData?.statusDescription && (
        <>
          <InfoBlock
            isFirst
            icon={<AiStar color='var(--text-primary)' width={16} height={16} />}
            title='Delay notification by AI assistant - Follow up needed'
            time='11 hrs ago'
            text='The AI assistant called Rob (destination contact) notifying him of the shipment delay...'
          />

          <InfoBlock
            avatar='JS'
            title='John Smith marked as resolved'
            time='now'
          />
        </>
      )}
    </Fragment>
  );
};

export default AiCallTranscript;
